import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { adminAPI } from '../lib/api';

// Superadmin: every account and every person, with everything we hold on each,
// and the repairs support needs — merging an account someone opened by mistake
// into the right one, merging a person who registered twice, deleting what is
// empty. ?org=<id> and ?user=<id> open a detail view, so a link can be shared.
// The server enforces every guard; this page explains them before you hit one.

const STATUS_COLOURS = {
  trial: 'text-blue-400',
  active: 'text-green-400',
  past_due: 'text-red-400',
  cancelled: 'text-orange-400',
  trial_expired: 'text-red-400',
};

const ROLE_BADGE = {
  owner: 'bg-purple-500/20 text-purple-300',
  admin: 'bg-blue-500/20 text-blue-400',
  member: 'bg-[#2a2a2a] text-gray-400',
  guest: 'bg-amber-500/15 text-amber-400',
};

const inputCls = 'w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';
const btnQuiet = 'px-3 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] rounded text-xs text-gray-300 transition-colors disabled:opacity-40';
const btnPrimary = 'px-3 py-2 bg-blue-700 hover:bg-blue-600 rounded text-xs font-medium text-white transition-colors disabled:opacity-40';
const btnDanger = 'px-3 py-2 bg-red-700 hover:bg-red-600 rounded text-xs font-medium text-white transition-colors disabled:opacity-40';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function money(amount) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount);
}

const errorText = (err, fallback) => err.response?.data?.error || err.message || fallback;

// ── Building blocks ─────────────────────────────────────────────────────────

function Section({ title, count, actions, children }) {
  return (
    <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg p-5 mb-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}{count !== undefined && <span className="text-gray-600 ml-1">({count})</span>}
        </h3>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

function Fields({ rows }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {rows.filter(Boolean).map(([label, value]) => (
        <div key={label} className="flex gap-3 min-w-0">
          <dt className="text-gray-500 w-36 flex-shrink-0">{label}</dt>
          <dd className="text-gray-200 min-w-0 break-words">{value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

function Id({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-gray-600">—</span>;
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); }).catch(() => {}); }}
      className="font-mono text-xs text-gray-400 hover:text-white break-all text-left"
      title="Copy"
    >
      {copied ? 'Copied' : value}
    </button>
  );
}

function Badge({ children, className = 'bg-[#2a2a2a] text-gray-400' }) {
  return <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${className}`}>{children}</span>;
}

function Table({ columns, rows, empty = 'None' }) {
  if (!rows.length) return <p className="text-sm text-gray-600">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 text-xs uppercase tracking-wider border-b border-[#2a2a2a]">
            {columns.map(c => <th key={c.label} className="py-2 pr-4 font-medium">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} className="border-b border-[#1a1a1a] last:border-0 align-top">
              {columns.map(c => <td key={c.label} className="py-2 pr-4 text-gray-300">{c.render(row)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PersonLink({ person, onOpen }) {
  if (!person) return <span className="text-gray-600">—</span>;
  return (
    <button onClick={() => onOpen(person.id)} className="text-left hover:text-white">
      <span className="text-gray-200">{person.name || person.email}</span>
      {person.name && <span className="block text-xs text-gray-500">{person.email}</span>}
    </button>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" onMouseDown={onClose}>
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg p-6 w-[30rem] max-w-full max-h-[90vh] overflow-y-auto" onMouseDown={e => e.stopPropagation()}>
        <h3 className="font-semibold mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

/** Search people and pick one. */
function PersonPicker({ excludeIds = [], onPick, picked }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      adminAPI.users(q.trim()).then(r => setResults(r.data.filter(u => !excludeIds.includes(u.id)).slice(0, 8))).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  if (picked) {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2 bg-[#0a0a0a] border border-blue-800 rounded">
        <div className="min-w-0">
          <div className="text-sm text-white truncate">{picked.name || picked.email}</div>
          {picked.name && <div className="text-xs text-gray-500 truncate">{picked.email}</div>}
        </div>
        <button onClick={() => onPick(null)} className="text-xs text-gray-500 hover:text-white">Change</button>
      </div>
    );
  }

  return (
    <div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or email" autoFocus className={inputCls} />
      {results.length > 0 && (
        <div className="mt-1 border border-[#2a2a2a] rounded divide-y divide-[#1a1a1a]">
          {results.map(u => (
            <button key={u.id} onClick={() => onPick(u)} className="w-full text-left px-3 py-2 hover:bg-[#1a1a1a]">
              <div className="text-sm text-gray-200">{u.name || u.email}</div>
              <div className="text-xs text-gray-500">
                {u.name ? `${u.email} · ` : ''}{u.accounts.map(a => `${a.name} (${a.role})`).join(', ') || 'no account'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Typed confirmation for anything that cannot be undone. */
function ConfirmByTyping({ expected, value, onChange }) {
  return (
    <div className="mb-4">
      <label className="block text-xs text-gray-400 mb-1">Type <span className="text-white font-mono">{expected}</span> to confirm</label>
      <input value={value} onChange={e => onChange(e.target.value)} className={inputCls} />
    </div>
  );
}

// ── Account repairs ─────────────────────────────────────────────────────────

function EditOrgModal({ org, onClose, onSaved }) {
  const [name, setName] = useState(org.name);
  const [plan, setPlan] = useState(org.plan);
  const [status, setStatus] = useState(org.subscription_status);
  const [trialDate, setTrialDate] = useState(org.trial_ends_at ? org.trial_ends_at.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    setSaving(true);
    setError('');
    try {
      await adminAPI.updateOrganisation(org.id, {
        name: name.trim(),
        plan,
        subscription_status: status,
        trial_ends_at: trialDate ? new Date(`${trialDate}T23:59:59`).toISOString() : null,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err, 'Failed to save'));
      setSaving(false);
    }
  }

  return (
    <Modal title={`Edit ${org.name}`} onClose={onClose}>
      <label className="block text-xs text-gray-400 mb-1">Name</label>
      <input value={name} onChange={e => setName(e.target.value)} className={`${inputCls} mb-3`} />
      <label className="block text-xs text-gray-400 mb-1">Plan</label>
      <select value={plan} onChange={e => setPlan(e.target.value)} className={`${inputCls} mb-3`}>
        <option value="starter">Starter</option>
        <option value="pro">Pro</option>
      </select>
      <label className="block text-xs text-gray-400 mb-1">Subscription status</label>
      <select value={status} onChange={e => setStatus(e.target.value)} className={`${inputCls} mb-3`}>
        <option value="trial">trial</option>
        <option value="active">active</option>
        <option value="past_due">past_due</option>
        <option value="cancelled">cancelled</option>
      </select>
      <label className="block text-xs text-gray-400 mb-1">Trial ends</label>
      <input type="date" value={trialDate} onChange={e => setTrialDate(e.target.value)} className={`${inputCls} mb-4`} />
      <p className="text-xs text-gray-600 mb-4">Status here is the app&rsquo;s record. It does not change anything in Stripe.</p>
      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className={btnQuiet}>Cancel</button>
        <button onClick={save} disabled={saving || !name.trim()} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

function AddMemberModal({ org, onClose, onDone }) {
  const [picked, setPicked] = useState(null);
  const [role, setRole] = useState('member');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function add() {
    setSaving(true);
    setError('');
    try {
      await adminAPI.addMember(org.id, picked.id, role);
      await onDone();
      onClose();
    } catch (err) {
      setError(errorText(err, 'Failed to add'));
      setSaving(false);
    }
  }

  return (
    <Modal title={`Add a person to ${org.name}`} onClose={onClose}>
      <div className="mb-3">
        <PersonPicker excludeIds={org.members.map(m => m.id)} picked={picked} onPick={setPicked} />
      </div>
      <label className="block text-xs text-gray-400 mb-1">Role</label>
      <select value={role} onChange={e => setRole(e.target.value)} className={`${inputCls} mb-3`}>
        <option value="member">Member</option>
        <option value="admin">Admin</option>
        <option value="owner">Owner</option>
      </select>
      <p className="text-xs text-gray-500 mb-4 leading-relaxed">
        Adds a seat. Their own account, if they have one, is untouched — to bring its shows here too, merge that account into this one instead.
      </p>
      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className={btnQuiet}>Cancel</button>
        <button onClick={add} disabled={saving || !picked} className={btnPrimary}>{saving ? 'Adding…' : 'Add to account'}</button>
      </div>
    </Modal>
  );
}

function MergeOrgModal({ org, orgs, onClose, onMerged }) {
  const others = orgs.filter(o => o.id !== org.id);
  const [targetId, setTargetId] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const target = others.find(o => o.id === targetId);
  const showCount = org.workspaces.reduce((n, w) => n + w.counts.shows, 0);
  const billed = org.stripe_subscription_id && org.subscription_status !== 'cancelled';

  async function merge() {
    setSaving(true);
    setError('');
    try {
      await adminAPI.mergeOrganisation(org.id, targetId, memberRole);
      onMerged(targetId);
    } catch (err) {
      setError(errorText(err, 'Failed to merge'));
      setSaving(false);
    }
  }

  return (
    <Modal title={`Merge ${org.name} into another account`} onClose={onClose}>
      <label className="block text-xs text-gray-400 mb-1">Keep this account</label>
      <select value={targetId} onChange={e => setTargetId(e.target.value)} className={`${inputCls} mb-3`}>
        <option value="">Choose…</option>
        {others.map(o => (
          <option key={o.id} value={o.id}>
            {o.name}{o.owners[0] ? ` — ${o.owners[0].name || o.owners[0].email}` : ''}
          </option>
        ))}
      </select>

      <label className="block text-xs text-gray-400 mb-1">{org.name}&rsquo;s members join as</label>
      <select value={memberRole} onChange={e => setMemberRole(e.target.value)} className={`${inputCls} mb-3`}>
        <option value="member">Members</option>
        <option value="keep">Their current roles (owners stay owners)</option>
      </select>

      <div className="text-xs text-gray-400 bg-[#0a0a0a] border border-[#1a1a1a] rounded p-3 mb-3 leading-relaxed space-y-1">
        <p>
          Moves {org.workspaces.length} workspace{org.workspaces.length === 1 ? '' : 's'} ({showCount} show{showCount === 1 ? '' : 's'}),{' '}
          {org.members.length} member{org.members.length === 1 ? '' : 's'} and {org.invites.length} pending invite{org.invites.length === 1 ? '' : 's'}
          {target ? <> into <span className="text-white">{target.name}</span></> : ''}.
        </p>
        <p>Anyone already in both keeps the more senior role. Guest passes into the kept account&rsquo;s workspaces are folded into membership.</p>
        <p>{org.name} is then deleted. This cannot be undone.</p>
      </div>

      {billed && (
        <p className="text-xs text-amber-400 mb-3 leading-relaxed">
          {org.name} still has a Stripe subscription ({org.subscription_status}). Cancel it in Stripe and mark this account
          cancelled first — the merge will be refused until then.
        </p>
      )}

      <ConfirmByTyping expected={org.name} value={confirm} onChange={setConfirm} />
      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className={btnQuiet}>Cancel</button>
        <button onClick={merge} disabled={saving || !targetId || confirm !== org.name} className={btnDanger}>
          {saving ? 'Merging…' : 'Merge and delete this account'}
        </button>
      </div>
    </Modal>
  );
}

function DeleteOrgModal({ org, onClose, onDeleted }) {
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const showCount = org.workspaces.reduce((n, w) => n + w.counts.shows, 0);

  async function remove() {
    setSaving(true);
    setError('');
    try {
      await adminAPI.deleteOrganisation(org.id);
      onDeleted();
    } catch (err) {
      setError(errorText(err, 'Failed to delete'));
      setSaving(false);
    }
  }

  return (
    <Modal title={`Delete ${org.name}`} onClose={onClose}>
      {showCount > 0 ? (
        <p className="text-sm text-amber-400 mb-4 leading-relaxed">
          This account holds {showCount} show{showCount === 1 ? '' : 's'}, so it can&rsquo;t be deleted. Merge it into another
          account, or move its workspaces to one, first.
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-400 mb-4 leading-relaxed">
            Deletes the account, its {org.workspaces.length} empty workspace{org.workspaces.length === 1 ? '' : 's'} and{' '}
            {org.members.length} membership{org.members.length === 1 ? '' : 's'}. The people themselves stay. This cannot be undone.
          </p>
          <ConfirmByTyping expected={org.name} value={confirm} onChange={setConfirm} />
        </>
      )}
      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className={btnQuiet}>Close</button>
        {showCount === 0 && (
          <button onClick={remove} disabled={saving || confirm !== org.name} className={btnDanger}>
            {saving ? 'Deleting…' : 'Delete account'}
          </button>
        )}
      </div>
    </Modal>
  );
}

// ── Person repairs ──────────────────────────────────────────────────────────

function EditUserModal({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    setSaving(true);
    setError('');
    try {
      await adminAPI.updateUser(user.id, { name: name.trim(), email: email.trim() });
      await onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err, 'Failed to save'));
      setSaving(false);
    }
  }

  return (
    <Modal title="Edit person" onClose={onClose}>
      <label className="block text-xs text-gray-400 mb-1">Name</label>
      <input value={name} onChange={e => setName(e.target.value)} className={`${inputCls} mb-3`} />
      <label className="block text-xs text-gray-400 mb-1">Email</label>
      <input value={email} onChange={e => setEmail(e.target.value)} className={`${inputCls} mb-2`} />
      <p className="text-xs text-gray-600 mb-4">Changing the email changes what they sign in with.</p>
      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className={btnQuiet}>Cancel</button>
        <button onClick={save} disabled={saving || !email.trim()} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

function MergeUserModal({ user, onClose, onMerged }) {
  const [picked, setPicked] = useState(null);
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function merge() {
    setSaving(true);
    setError('');
    try {
      await adminAPI.mergeUser(user.id, picked.id);
      onMerged(picked.id);
    } catch (err) {
      setError(errorText(err, 'Failed to merge'));
      setSaving(false);
    }
  }

  return (
    <Modal title={`Merge ${user.name || user.email} into another person`} onClose={onClose}>
      <label className="block text-xs text-gray-400 mb-1">Keep this person</label>
      <div className="mb-3">
        <PersonPicker excludeIds={[user.id]} picked={picked} onPick={setPicked} />
      </div>
      <div className="text-xs text-gray-400 bg-[#0a0a0a] border border-[#1a1a1a] rounded p-3 mb-3 leading-relaxed space-y-1">
        <p>
          {user.accounts.length} account membership{user.accounts.length === 1 ? '' : 's'}, {user.guest_access.length} guest
          pass{user.guest_access.length === 1 ? '' : 'es'} and {user.show_roles.length} show role{user.show_roles.length === 1 ? '' : 's'} move
          across — the more senior role wins. Their history is kept under the other person.
        </p>
        {user.sign_in.includes('google') && <p>Their Google sign-in moves over if the other person has none.</p>}
        <p>
          <span className="text-white">{user.email}</span> is then deleted and can no longer be used to sign in. Their Claude
          tokens and connectors stop working. An account they owned is not deleted — review it afterwards.
        </p>
      </div>
      <ConfirmByTyping expected={user.email} value={confirm} onChange={setConfirm} />
      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className={btnQuiet}>Cancel</button>
        <button onClick={merge} disabled={saving || !picked || confirm !== user.email} className={btnDanger}>
          {saving ? 'Merging…' : 'Merge and delete this person'}
        </button>
      </div>
    </Modal>
  );
}

function DeleteUserModal({ user, onClose, onDeleted }) {
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function remove() {
    setSaving(true);
    setError('');
    try {
      await adminAPI.deleteUser(user.id);
      onDeleted();
    } catch (err) {
      setError(errorText(err, 'Failed to delete'));
      setSaving(false);
    }
  }

  return (
    <Modal title={`Delete ${user.name || user.email}`} onClose={onClose}>
      <p className="text-sm text-gray-400 mb-3 leading-relaxed">
        Removes their sign-in, account memberships, guest passes, show roles and Claude tokens. Their show history stays,
        without their name. An account they own alone that holds no shows is deleted with them.
      </p>
      <p className="text-xs text-gray-500 mb-4">
        If this is a duplicate of someone, merge instead — that keeps their accounts and history with the other person.
      </p>
      <ConfirmByTyping expected={user.email} value={confirm} onChange={setConfirm} />
      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className={btnQuiet}>Cancel</button>
        <button onClick={remove} disabled={saving || confirm !== user.email} className={btnDanger}>
          {saving ? 'Deleting…' : 'Delete person'}
        </button>
      </div>
    </Modal>
  );
}

// ── Organisation detail ─────────────────────────────────────────────────────

function OrgDetail({ id, orgs, onOpenUser, onOpenOrg, onChanged, onGone }) {
  const [org, setOrg] = useState(null);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [modal, setModal] = useState(null); // 'edit' | 'add' | 'merge' | 'delete'
  const [moveTargets, setMoveTargets] = useState({});

  const load = () => adminAPI.organisation(id)
    .then(r => setOrg(r.data))
    .catch(err => setError(errorText(err, 'Failed to load organisation')));

  const refresh = async () => { await load(); onChanged(); };

  useEffect(() => { setOrg(null); setError(''); load(); }, [id]);

  async function act(fn, fallback) {
    setActionError('');
    try {
      await fn();
      await refresh();
    } catch (err) {
      setActionError(errorText(err, fallback));
    }
  }

  if (error) return <p className="text-red-400 text-sm">{error}</p>;
  if (!org) return <p className="text-gray-600 text-sm">Loading…</p>;

  const trialOver = org.subscription_status === 'trial' && org.trial_ends_at && new Date(org.trial_ends_at) < new Date();
  const otherOrgs = orgs.filter(o => o.id !== org.id);

  return (
    <>
      {modal === 'edit' && <EditOrgModal org={org} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === 'add' && <AddMemberModal org={org} onClose={() => setModal(null)} onDone={refresh} />}
      {modal === 'merge' && (
        <MergeOrgModal org={org} orgs={orgs} onClose={() => setModal(null)} onMerged={(targetId) => { onChanged(); onOpenOrg(targetId); }} />
      )}
      {modal === 'delete' && <DeleteOrgModal org={org} onClose={() => setModal(null)} onDeleted={() => { onChanged(); onGone(); }} />}

      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold">{org.name}</h2>
          <div className="text-sm mt-1">
            <span className={STATUS_COLOURS[trialOver ? 'trial_expired' : org.subscription_status] || 'text-gray-400'}>
              {trialOver ? 'trial expired' : org.subscription_status}
            </span>
            <span className="text-gray-600"> · </span>
            <span className="text-gray-400 capitalize">{org.plan}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setModal('edit')} className={btnQuiet}>Edit</button>
          <button onClick={() => setModal('merge')} className={btnQuiet}>Merge into…</button>
          <button onClick={() => setModal('delete')} className={`${btnQuiet} hover:text-red-400`}>Delete</button>
        </div>
      </div>

      {actionError && (
        <div className="bg-red-900/30 border border-red-700 rounded px-4 py-3 text-red-300 text-sm mb-4">{actionError}</div>
      )}

      <Section title="Account and billing">
        <Fields rows={[
          ['Organisation id', <Id value={org.id} />],
          ['Created', formatDateTime(org.created_at)],
          ['Last updated', formatDateTime(org.updated_at)],
          ['Trial ends', formatDate(org.trial_ends_at)],
          ['Seats', `${org.seats} × ${money(org.seat_price.amount)} = ${money(org.monthly_total)}/month`],
          ['Stripe customer', <Id value={org.stripe_customer_id} />],
          ['Stripe subscription', <Id value={org.stripe_subscription_id} />],
          ['Last activity', formatDateTime(org.activity.last_event_at)],
          ['MCP calls (30 days)', org.activity.mcp_calls_30d],
        ]} />
      </Section>

      <Section title="Members" count={org.members.length} actions={<button onClick={() => setModal('add')} className={btnQuiet}>Add person</button>}>
        <Table
          rows={org.members}
          columns={[
            { label: 'Person', render: m => <PersonLink person={m} onOpen={onOpenUser} /> },
            {
              label: 'Role',
              render: m => (
                <select
                  value={m.role}
                  onChange={e => act(() => adminAPI.setMemberRole(org.id, m.id, e.target.value), 'Failed to change the role')}
                  className="bg-[#1a1a1a] border border-[#2a2a2a] rounded px-2 py-1 text-xs text-gray-300"
                >
                  <option value="owner">owner</option>
                  <option value="admin">admin</option>
                  <option value="member">member</option>
                </select>
              ),
            },
            { label: 'Sign-in', render: m => m.sign_in.join(', ') || '—' },
            { label: 'Joined', render: m => formatDate(m.joined_at) },
            { label: '', render: m => m.is_superadmin ? <Badge className="bg-red-500/20 text-red-400">superadmin</Badge> : null },
            {
              label: ' ',
              render: m => (
                <button
                  onClick={() => { if (confirm(`Remove ${m.name || m.email} from ${org.name}?`)) act(() => adminAPI.removeMember(org.id, m.id), 'Failed to remove'); }}
                  className="text-xs text-gray-500 hover:text-red-400"
                >
                  Remove
                </button>
              ),
            },
          ]}
        />
      </Section>

      {org.workspaces.map(ws => (
        <Section
          key={ws.id}
          title={`Workspace — ${ws.name}`}
          actions={otherOrgs.length > 0 && (
            <>
              <select
                value={moveTargets[ws.id] || ''}
                onChange={e => setMoveTargets(prev => ({ ...prev, [ws.id]: e.target.value }))}
                className="bg-[#1a1a1a] border border-[#2a2a2a] rounded px-2 py-1.5 text-xs text-gray-300 max-w-[12rem]"
              >
                <option value="">Move to account…</option>
                {otherOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <button
                disabled={!moveTargets[ws.id]}
                onClick={() => {
                  const target = otherOrgs.find(o => o.id === moveTargets[ws.id]);
                  if (!confirm(`Move ${ws.name} and its ${ws.counts.shows} show(s) to ${target.name}? ${org.name}'s members lose access to it.`)) return;
                  act(() => adminAPI.moveWorkspace(ws.id, target.id), 'Failed to move the workspace');
                }}
                className={btnQuiet}
              >
                Move
              </button>
            </>
          )}
        >
          <Fields rows={[
            ['Workspace id', <Id value={ws.id} />],
            ['Created', formatDate(ws.created_at)],
            ['Shows', ws.counts.shows],
            ['Cue blocks', ws.counts.cue_blocks],
            ['vMix instances / presets', `${ws.counts.vmix_instances} / ${ws.counts.vmix_presets}`],
            ['Tool presets / runs', `${ws.counts.tool_presets} / ${ws.counts.tool_runs}`],
          ]} />

          <div className="mt-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Shows</div>
          <Table
            rows={ws.shows}
            empty="No shows"
            columns={[
              { label: 'Show', render: s => <span className="text-gray-200">{s.name}</span> },
              { label: 'Status', render: s => s.status },
              { label: 'Cues', render: s => s.cue_count },
              { label: 'Scheduled', render: s => formatDateTime(s.scheduled_start) },
              { label: 'Last aired', render: s => formatDateTime(s.actual_start) },
              { label: 'Updated', render: s => formatDate(s.updated_at) },
            ]}
          />

          <div className="mt-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Guests</div>
          <Table
            rows={ws.guests}
            empty="No guests"
            columns={[
              { label: 'Guest', render: g => <PersonLink person={g} onOpen={onOpenUser} /> },
              { label: 'Access ends', render: g => g.expires_at ? <span className={g.expired ? 'text-red-400' : ''}>{formatDate(g.expires_at)}</span> : 'No end date' },
              { label: 'Invited by', render: g => g.invited_by ? (g.invited_by.name || g.invited_by.email) : '—' },
              { label: 'Added', render: g => formatDate(g.added_at) },
            ]}
          />
        </Section>
      ))}

      <Section title="Pending invites" count={org.invites.length}>
        <Table
          rows={org.invites}
          empty="No pending invites"
          columns={[
            { label: 'Email', render: i => i.email },
            { label: 'For', render: i => i.kind === 'member' ? `Member (${i.role})` : `Guest — ${i.workspace_name || '?'}` },
            { label: 'Invited by', render: i => i.invited_by ? (i.invited_by.name || i.invited_by.email) : '—' },
            { label: 'Sent', render: i => formatDate(i.created_at) },
            { label: 'Expires', render: i => <span className={i.expired ? 'text-red-400' : ''}>{formatDate(i.expires_at)}</span> },
          ]}
        />
      </Section>

      <Section title="Recent show activity">
        <Table
          rows={org.activity.recent}
          empty="No activity yet"
          columns={[
            { label: 'When', render: e => formatDateTime(e.at) },
            { label: 'Event', render: e => <span className="font-mono text-xs">{e.event_type}</span> },
            { label: 'Show', render: e => e.show_name || '—' },
            { label: 'By', render: e => e.by ? <PersonLink person={e.by} onOpen={onOpenUser} /> : '—' },
          ]}
        />
      </Section>
    </>
  );
}

// ── Person detail ───────────────────────────────────────────────────────────

function UserDetail({ id, onOpenOrg, onOpenUser, onChanged, onGone }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // 'edit' | 'merge' | 'delete'

  const load = () => adminAPI.user(id)
    .then(r => setUser(r.data))
    .catch(err => setError(errorText(err, 'Failed to load person')));

  useEffect(() => { setUser(null); setError(''); load(); }, [id]);

  if (error) return <p className="text-red-400 text-sm">{error}</p>;
  if (!user) return <p className="text-gray-600 text-sm">Loading…</p>;

  const OrgLink = ({ org }) => org
    ? <button onClick={() => onOpenOrg(org.id)} className="text-gray-200 hover:text-white text-left">{org.name}</button>
    : <span className="text-gray-600">—</span>;

  return (
    <>
      {modal === 'edit' && <EditUserModal user={user} onClose={() => setModal(null)} onSaved={async () => { await load(); onChanged(); }} />}
      {modal === 'merge' && (
        <MergeUserModal user={user} onClose={() => setModal(null)} onMerged={(targetId) => { onChanged(); onOpenUser(targetId); }} />
      )}
      {modal === 'delete' && <DeleteUserModal user={user} onClose={() => setModal(null)} onDeleted={() => { onChanged(); onGone(); }} />}

      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          {user.avatar_url
            ? <img src={user.avatar_url} alt="" className="w-10 h-10 rounded-full" />
            : <div className="w-10 h-10 rounded-full bg-[#2a2a2a] flex items-center justify-center text-gray-400">{(user.name || user.email)[0].toUpperCase()}</div>}
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              {user.name || user.email}
              {user.is_superadmin && <Badge className="bg-red-500/20 text-red-400">superadmin</Badge>}
            </h2>
            {user.name && <div className="text-sm text-gray-500">{user.email}</div>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setModal('edit')} className={btnQuiet}>Edit</button>
          <button onClick={() => setModal('merge')} className={btnQuiet}>Merge into…</button>
          <button onClick={() => setModal('delete')} className={`${btnQuiet} hover:text-red-400`}>Delete</button>
        </div>
      </div>

      <Section title="Profile">
        <Fields rows={[
          ['User id', <Id value={user.id} />],
          ['Email', user.email],
          ['Signed up', formatDateTime(user.created_at)],
          ['Last updated', formatDateTime(user.updated_at)],
          ['Sign-in methods', user.sign_in.join(', ') || 'none'],
          ['As a guest', user.guest_level === 'full' ? 'Full production access (pays for an account)' : 'Text and running the show'],
          ['Last open workspace', user.last_workspace ? `${user.last_workspace.name}${user.last_workspace.organisation ? ` (${user.last_workspace.organisation.name})` : ''}` : 'None'],
          ['Invites sent', user.invites_sent],
          ['MCP calls (all time)', user.activity.mcp_calls_total],
          ['Legacy org / role', `${user.legacy.organisation_id || '—'} / ${user.legacy.global_role}`],
        ]} />
      </Section>

      <Section title="Accounts" count={user.accounts.length}>
        <Table
          rows={user.accounts}
          empty="Not a member of any account"
          columns={[
            { label: 'Account', render: a => <OrgLink org={a} /> },
            { label: 'Role', render: a => <Badge className={ROLE_BADGE[a.role]}>{a.role}</Badge> },
            { label: 'Shows', render: a => a.show_count },
            { label: 'Workspaces', render: a => a.workspace_count },
            { label: 'Status', render: a => <span className={STATUS_COLOURS[a.subscription_status] || ''}>{a.subscription_status}</span> },
            { label: 'Seats', render: a => a.seats },
            { label: 'Joined', render: a => formatDate(a.joined_at) },
          ]}
        />
      </Section>

      <Section title="Guest access" count={user.guest_access.length}>
        <Table
          rows={user.guest_access}
          empty="Not a guest anywhere"
          columns={[
            { label: 'Workspace', render: g => g.workspace_name },
            { label: 'Account', render: g => <OrgLink org={g.organisation} /> },
            { label: 'Access ends', render: g => g.expires_at ? <span className={g.expired ? 'text-red-400' : ''}>{formatDate(g.expires_at)}</span> : 'No end date' },
            { label: 'Invited by', render: g => g.invited_by ? (g.invited_by.name || g.invited_by.email) : '—' },
            { label: 'Added', render: g => formatDate(g.added_at) },
          ]}
        />
      </Section>

      <Section title="Pending invites to this address" count={user.pending_invites.length}>
        <Table
          rows={user.pending_invites}
          empty="None"
          columns={[
            { label: 'For', render: i => i.kind === 'member' ? `Member (${i.role}) of ${i.organisation.name}` : `Guest — ${i.workspace_name} (${i.organisation.name})` },
            { label: 'Invited by', render: i => i.invited_by ? (i.invited_by.name || i.invited_by.email) : '—' },
            { label: 'Sent', render: i => formatDate(i.created_at) },
            { label: 'Expires', render: i => <span className={i.expired ? 'text-red-400' : ''}>{formatDate(i.expires_at)}</span> },
          ]}
        />
      </Section>

      <Section title="Show roles" count={user.show_roles.length}>
        <Table
          rows={user.show_roles}
          empty="None"
          columns={[
            { label: 'Show', render: s => <span className={s.deleted ? 'text-gray-600 line-through' : 'text-gray-200'}>{s.show_name}</span> },
            { label: 'Workspace', render: s => s.workspace_name || '—' },
            { label: 'Status', render: s => s.status },
            { label: 'Role', render: s => s.role },
          ]}
        />
      </Section>

      <Section title="Claude tokens" count={user.mcp_tokens.length}>
        <Table
          rows={user.mcp_tokens}
          empty="No MCP tokens"
          columns={[
            { label: 'Name', render: t => t.name },
            { label: 'Scopes', render: t => <span className="font-mono text-xs">{t.scopes.join(' · ')}</span> },
            { label: 'Status', render: t => <span className={t.status === 'active' ? 'text-green-400' : 'text-red-400'}>{t.status}</span> },
            { label: 'Last used', render: t => formatDateTime(t.last_used_at) },
            { label: 'Created', render: t => formatDate(t.created_at) },
            { label: 'Expires', render: t => t.expires_at ? formatDate(t.expires_at) : 'Never' },
          ]}
        />
      </Section>

      <Section title="Claude connectors" count={user.oauth_connections.length}>
        <Table
          rows={user.oauth_connections}
          empty="No active connectors"
          columns={[
            { label: 'Client', render: c => c.client_name },
            { label: 'Scopes', render: c => <span className="font-mono text-xs">{c.scopes.join(' · ')}</span> },
            { label: 'Connected', render: c => formatDate(c.connected_at) },
            { label: 'Refresh expires', render: c => formatDate(c.expires_at) },
            { label: 'Sessions', render: c => c.sessions },
          ]}
        />
      </Section>

      <Section title="Recent show activity">
        <Table
          rows={user.activity.show_events}
          empty="None"
          columns={[
            { label: 'When', render: e => formatDateTime(e.at) },
            { label: 'Event', render: e => <span className="font-mono text-xs">{e.event_type}</span> },
            { label: 'Show', render: e => e.show_name || '—' },
            { label: 'Workspace', render: e => e.workspace_name || '—' },
          ]}
        />
      </Section>

      <Section title="Recent tool runs">
        <Table
          rows={user.activity.tool_runs}
          empty="None"
          columns={[
            { label: 'Started', render: r => formatDateTime(r.started_at) },
            { label: 'Tool', render: r => r.tool_key },
            { label: 'Status', render: r => <span title={r.error_message || undefined} className={r.status === 'failed' ? 'text-red-400' : ''}>{r.status}</span> },
            { label: 'Workspace', render: r => r.workspace_name || '—' },
          ]}
        />
      </Section>

      <Section title="Recent MCP calls">
        <Table
          rows={user.activity.mcp_calls}
          empty="None"
          columns={[
            { label: 'When', render: c => formatDateTime(c.at) },
            { label: 'Tool', render: c => <span className="font-mono text-xs">{c.tool_name}</span> },
            { label: 'Risk', render: c => c.risk },
            { label: 'Status', render: c => <span className={c.status === 'ok' ? 'text-green-400' : 'text-red-400'}>{c.status}</span> },
            { label: 'Workspace', render: c => c.workspace_name || '—' },
          ]}
        />
      </Section>
    </>
  );
}

// ── Lists ───────────────────────────────────────────────────────────────────

function OrgList({ orgs, loading, onOpen }) {
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();
  const shown = q
    ? orgs.filter(o => o.name.toLowerCase().includes(q) || o.owners.some(p => p.email.toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q)))
    : orgs;

  return (
    <>
      <input
        value={filter}
        onChange={e => setFilter(e.target.value)}
        placeholder="Filter by account name or owner"
        className="w-full mb-4 bg-[#111111] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
      />
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#2a2a2a] text-gray-500 text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3">Account</th>
              <th className="text-left px-4 py-3">Owner</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Trial ends</th>
              <th className="text-left px-4 py-3">Seats</th>
              <th className="text-left px-4 py-3">Monthly</th>
              <th className="text-left px-4 py-3">Workspaces</th>
              <th className="text-left px-4 py-3">Shows</th>
              <th className="text-left px-4 py-3">Guests</th>
              <th className="text-left px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-600">Loading...</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-600">No accounts.</td></tr>
            ) : shown.map(org => (
              <tr key={org.id} onClick={() => onOpen(org.id)} className="border-b border-[#1a1a1a] hover:bg-[#161616] transition-colors cursor-pointer">
                <td className="px-4 py-3 font-medium">{org.name}<span className="block text-xs text-gray-600 capitalize">{org.plan}</span></td>
                <td className="px-4 py-3 text-gray-400">{org.owners[0] ? (org.owners[0].name || org.owners[0].email) : '—'}</td>
                <td className={`px-4 py-3 ${STATUS_COLOURS[org.subscription_status] || 'text-gray-400'}`}>
                  {org.subscription_status}{org.has_subscription && <span className="text-gray-600 text-xs"> · Stripe</span>}
                </td>
                <td className="px-4 py-3 text-gray-500">{formatDate(org.trial_ends_at)}</td>
                <td className="px-4 py-3 text-gray-400">{org.seats}</td>
                <td className="px-4 py-3 text-gray-400">{money(org.monthly_total)}</td>
                <td className="px-4 py-3 text-gray-400">{org.workspace_count}</td>
                <td className="px-4 py-3 text-gray-400">{org.show_count}</td>
                <td className="px-4 py-3 text-gray-400">{org.guest_count}</td>
                <td className="px-4 py-3 text-gray-500">{formatDate(org.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UserList({ onOpen, refreshKey }) {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      adminAPI.users(query.trim() || undefined)
        .then(r => setUsers(r.data))
        .catch(err => setError(errorText(err, 'Failed to load people')));
    }, 250);
    return () => clearTimeout(t);
  }, [query, refreshKey]);

  // The same name on more than one person is usually someone who registered twice.
  const nameCounts = new Map();
  for (const u of users || []) {
    const key = (u.name || '').trim().toLowerCase();
    if (key) nameCounts.set(key, (nameCounts.get(key) || 0) + 1);
  }
  const isPossibleDuplicate = (u) => (nameCounts.get((u.name || '').trim().toLowerCase()) || 0) > 1;

  return (
    <>
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search by email or name"
        className="w-full mb-4 bg-[#111111] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
      />
      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#2a2a2a] text-gray-500 text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3">Person</th>
              <th className="text-left px-4 py-3">Accounts</th>
              <th className="text-left px-4 py-3">Guest in</th>
              <th className="text-left px-4 py-3">Sign-in</th>
              <th className="text-left px-4 py-3">Tokens</th>
              <th className="text-left px-4 py-3">Last workspace</th>
              <th className="text-left px-4 py-3">Signed up</th>
            </tr>
          </thead>
          <tbody>
            {users === null ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-600">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-600">No one matches.</td></tr>
            ) : users.map(u => (
              <tr key={u.id} onClick={() => onOpen(u.id)} className="border-b border-[#1a1a1a] hover:bg-[#161616] transition-colors cursor-pointer align-top">
                <td className="px-4 py-3">
                  <span className="text-gray-200">{u.name || u.email}</span>
                  {u.is_superadmin && <Badge className="ml-2 bg-red-500/20 text-red-400">superadmin</Badge>}
                  {isPossibleDuplicate(u) && <Badge className="ml-2 bg-amber-500/15 text-amber-400">possible duplicate</Badge>}
                  {u.name && <span className="block text-xs text-gray-500">{u.email}</span>}
                </td>
                <td className="px-4 py-3">
                  {u.accounts.length === 0 ? <span className="text-gray-600">—</span> : u.accounts.map(a => (
                    <div key={a.id} className="flex items-center gap-1.5 text-gray-300">
                      {a.name} <Badge className={ROLE_BADGE[a.role]}>{a.role}</Badge>
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3 text-gray-400">{u.guest_workspace_count || '—'}</td>
                <td className="px-4 py-3 text-gray-400">{u.sign_in.join(', ') || '—'}</td>
                <td className="px-4 py-3 text-gray-400">{u.mcp_token_count || '—'}</td>
                <td className="px-4 py-3 text-gray-500">{u.last_workspace?.name || '—'}</td>
                <td className="px-4 py-3 text-gray-500">{formatDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {users?.length === 500 && <p className="text-xs text-gray-600 mt-2">Showing the 500 most recent — search to narrow.</p>}
    </>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function Admin() {
  const navigate = useNavigate();
  const { isSuperadmin } = useAuthStore();
  const [params, setParams] = useSearchParams();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [peopleVersion, setPeopleVersion] = useState(0);

  const orgId = params.get('org');
  const userId = params.get('user');
  const tab = params.get('tab') === 'people' ? 'people' : 'accounts';

  const loadOrgs = () => adminAPI.organisations()
    .then(res => setOrgs(res.data))
    .catch(err => setError(errorText(err, 'Failed to load organisations')))
    .finally(() => setLoading(false));

  useEffect(() => {
    if (!isSuperadmin()) {
      navigate('/');
      return;
    }
    loadOrgs();
  }, []);

  const openOrg = (id) => setParams({ org: id });
  const openUser = (id) => setParams({ user: id });
  const showTab = (t) => setParams(t === 'people' ? { tab: 'people' } : {});
  const changed = () => { loadOrgs(); setPeopleVersion(v => v + 1); };

  // Summary stats
  const total = orgs.length;
  const active = orgs.filter(o => o.subscription_status === 'active').length;
  const trials = orgs.filter(o => o.subscription_status === 'trial').length;
  const seats = orgs.reduce((n, o) => n + o.seats, 0);
  const mrr = orgs.filter(o => o.subscription_status === 'active').reduce((n, o) => n + o.monthly_total, 0);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate('/')} className="text-gray-600 hover:text-white transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-red-600 rounded flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full" />
            </div>
            <span className="font-bold">Mission Control</span>
          </div>
          <span className="text-gray-600">/</span>
          <h1 className="font-semibold">Superadmin</h1>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded px-4 py-3 text-red-300 text-sm mb-6">{error}</div>
        )}

        {orgId || userId ? (
          <>
            <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-white mb-4">← Back</button>
            {orgId ? (
              <OrgDetail
                id={orgId}
                orgs={orgs}
                onOpenUser={openUser}
                onOpenOrg={openOrg}
                onChanged={changed}
                onGone={() => setParams({})}
              />
            ) : (
              <UserDetail
                id={userId}
                onOpenOrg={openOrg}
                onOpenUser={openUser}
                onChanged={changed}
                onGone={() => setParams({ tab: 'people' })}
              />
            )}
          </>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {[
                { label: 'Accounts', value: total },
                { label: 'Active', value: active, color: 'text-green-400' },
                { label: 'Trials', value: trials, color: 'text-blue-400' },
                { label: 'Seats', value: seats },
                { label: 'Active monthly', value: money(mrr), color: 'text-green-400' },
              ].map(s => (
                <div key={s.label} className="bg-[#111111] border border-[#2a2a2a] rounded-lg px-5 py-4">
                  <div className={`text-2xl font-bold ${s.color || ''}`}>{loading ? '—' : s.value}</div>
                  <div className="text-gray-500 text-xs mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-1 mb-4">
              {[['accounts', 'Accounts'], ['people', 'People']].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => showTab(key)}
                  className={`px-4 py-1.5 text-sm rounded transition-colors ${tab === key ? 'bg-[#1e1e1e] text-white' : 'text-gray-500 hover:text-gray-300'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'accounts'
              ? <OrgList orgs={orgs} loading={loading} onOpen={openOrg} />
              : <UserList onOpen={openUser} refreshKey={peopleVersion} />}
          </>
        )}
      </div>
    </div>
  );
}
